import styled from "styled-components";
import { font } from "../../lib/sharedStyles";

export const Figure = styled.figure<{
  $contentAlignment?: string;
  $isMobile: boolean;
  $mobileContentAlignment?: string;
  $theme?: string;
  $useBackgroundOverlay: boolean;
  $brand?: string;
  $dynamicHeight?: string;
  $minContentHeight?: number;
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: ${({ $isMobile, $mobileContentAlignment }) =>
    $isMobile && $mobileContentAlignment == 'bottom' ? 'flex-end' : 'center'};
  text-align: ${({ $isMobile, $contentAlignment }) =>
    $isMobile ? 'center' : $contentAlignment == 'left' ? 'left' : 'center'};
  position: relative;
  background-color: ${({ $theme }) => ($theme == 'dark' ? '#000' : '#fff')};
  margin: 0;
  padding: 0 28px;
  height: ${({ $dynamicHeight }) => $dynamicHeight || '432px'};
  min-height: 545px;

  @media (min-width: 1024px) {
    height: ${({ $dynamicHeight }) => $dynamicHeight || '600px'};
    min-height: 610px;
    padding-left: 72px;
    align-items: ${({ $contentAlignment }) =>
      $contentAlignment == 'left' ? 'flex-start' : 'center'};
    text-align: ${({ $contentAlignment }) =>
      $contentAlignment == 'left' ? 'left' : 'center'};
  }

  ${({ $contentAlignment }) => $contentAlignment == 'bottom left' && `
    @media (min-width: 1024px) {
      text-align: left;
      align-items: flex-start;
      justify-content: flex-end;
    }
  `}

  ${({ $useBackgroundOverlay, $theme, $mobileContentAlignment }) =>
    $useBackgroundOverlay &&
    `
      &:after {
        content: "";
        position: absolute;
        height: 100%;
        width: 100%;
        left: 0;
        top: 0;
        z-index: 1;
        background: ${
          $mobileContentAlignment == 'below'
            ? 'transparent'
            : $theme == 'dark'
              ? 'rgba(0, 0, 0, 0.5)'
              : 'rgba(255, 255, 255, 0.5)'
        };
      }

      @media (min-width: 1024px) {
        &:after {
          background: ${
            $theme == 'dark'
              ? 'rgba(0, 0, 0, 0.5)'
              : 'rgba(255, 255, 255, 0.5)'
          };
        }
      }
    `}
`;

export const BackgroundContainer = styled.div<{
  $transparentHeaderHeight: number;
  $useTransparentHeader: boolean;
}>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;

  ${({ $useTransparentHeader, $transparentHeaderHeight }) => $useTransparentHeader && $transparentHeaderHeight > 0 && `
    top: -${$transparentHeaderHeight}px;
    height: calc(100% + ${$transparentHeaderHeight}px);
  `}
`;

export const Video = styled.video<{ $objectPosition?: string }>`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: ${({ $objectPosition }) => $objectPosition || 'center center'};
  transition: opacity 0.4s ease;
`;

export const Image = styled.img<{ $objectPosition?: string }>`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: ${({ $objectPosition }) => $objectPosition || 'center center'};
  transition: opacity 0.4s ease;
`;

export const ContentWrapper = styled.div<{
  $brand?: string;
  $contentAlignment?: string;
  $isMobile: boolean;
  $maxWidth?: number;
  $mobileContentAlignment?: string;
  $theme?: string;
}>`
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: ${({ $mobileContentAlignment }) =>
    $mobileContentAlignment == 'bottom' ? 'flex-end' : 'center'};
  text-align: center;
  max-width: 100%;
  padding-bottom: ${({ $mobileContentAlignment }) =>
    ['bottom', 'below'].includes($mobileContentAlignment ?? '') ? '60px' : '0'};
  background-color: ${({ $brand, $theme, $mobileContentAlignment }) =>
    $mobileContentAlignment == 'below'
      ? $brand == 'presonus'
        ? ($theme == 'dark' ? '#161317' : '#EFEFEF')
        : $brand == 'jackson'
        ? ($theme == 'dark' ? '#000' : '#FFF')
        : $brand == 'fender'
        ? ($theme == 'dark' ? '#191A1F' : '#FFF')
        : ($theme == 'dark' ? '#000' : '#FFF')
      : undefined};

  p,
  h1 {
    margin: 0;
    margin-top: 16px;
  }

  ${({ $brand, $theme }) =>
    $brand == 'fender' &&
    `
    p, h1, h2, h3, h4, h5, h6 {
      color: ${$theme == 'dark' ? '#fff' : '#191A1F'};
      font-family: Futura;
    }

    /* Inline links inside text */
    p a, li a {
      color: ${$theme == 'dark' ? '#fff' : '#191A1F'};
      text-decoration: none;
      &:hover {
        text-decoration: underline;
      }
    }

    h1 {
      font-size: 24px;
      line-height: 20px;
      letter-spacing: -0.02em;
    }
    h2 {
      font-size: 24px;
      line-height: 20px;
      letter-spacing: -0.02em;
    }
    h3 {
      font-size: 16px;
      line-height: 12px;
      letter-spacing: -0.02em;
    }
    h4 {
      font-size: 10px;
      line-height: 6px;
      letter-spacing: -0.02em;
    }
    h5 {
      font-size: 36px;
      line-height: 32px;
      letter-spacing: -0.02em;
      margin: 16px auto;
    }

    @media (min-width: 1024px) {
      h1 {
        font-size: 48px;
        line-height: 44px;
        letter-spacing: -0.02em;
      }
      h2 {
        font-size: 36px;
        line-height: 32px;
        letter-spacing: -0.02em;
      }
      h3 {
        font-size: 24px;
        line-height: 18px;
        letter-spacing: -0.02em;
      }
      h4 {
        font-size: 14px;
        line-height: 10px;
        letter-spacing: -0.02em;
      }
      h5 {
        font-size: 64px;
        line-height: 58px;
        letter-spacing: -0.02em;
        margin: 16px auto;
      }
    }
  `}

  ${({ $brand, $theme }) => $brand == 'presonus' && `
    p, h1, h2, h3, h4, h5, h6, a {
      color: ${($theme == 'dark' ? '#fff' : '#161317')};
    }

    h6 {
      font-size: 14px;
      line-height: 18px;
    }
  `}

  ${({ $brand, $theme }) => $brand == 'jackson' && `
    font-family: 'Palanquin', sans-serif;
    font-weight: 400;
    font-style: normal;

    p, h1, h2, h3, h4, h5, h6, a {
      color: ${($theme == 'dark' ? '#fff' : '#000')};
    }

    p {
      max-width: 480px;
      font-size: 14px;
    }

    h1 {
      ${font('Palanquin Dark', 700, '2rem', '2.1rem')}
      letter-spacing: 0.01562rem;
      text-transform: uppercase;
      margin-bottom: 0.5rem;
      padding-bottom: 0;
      width: 100%;
    }

    h6 {
      ${font('Palanquin', 300, '1rem', '1.25rem')}
      letter-spacing: 0.3125rem;
      margin-bottom: 0.75rem;
      text-transform: uppercase;
      width: 100%;
    }

    @media (min-width: 1024px) {
      h1 {
        font-size: 2.75rem;
        line-height: 2.75rem;
      }
    }
  `}

  @media (min-width: 1024px) {
    align-items: ${({ $contentAlignment }) =>
      $contentAlignment == 'left' || $contentAlignment == 'bottom left' ? 'flex-start' : 'center'};
    text-align: ${({ $contentAlignment }) =>
      $contentAlignment == 'left' || $contentAlignment == 'bottom left' ? 'left' : 'center'};
    padding-bottom: ${({ $contentAlignment }) =>
      $contentAlignment == 'bottom left' ? '36px' : '0'};
    max-width: ${({ $maxWidth }) => ($maxWidth ? `${$maxWidth}px` : '100%')};
    justify-content: center;
    background-color: unset;
  }
`;

export const LogoImage = styled.img<{
  $maxWidth?: number;
  $theme: string;
  $disableFilter?: boolean;
}>`
  width: 100%;
  max-width: ${({ $maxWidth }) => ($maxWidth ? `${$maxWidth}px` : "100%")};
  padding: 1.5rem 1rem 0 1rem;

  ${({ $theme, $disableFilter }) =>
    !$disableFilter &&
    $theme == "dark" &&
    `
      filter: invert(1) brightness(2);
    `}

  @media (min-width: 1024px) {
    padding: 0;
  }
`;

export const Caption = styled.figcaption<{
  $brand?: string;
  $contentAlignment?: string;
  $isMobile: boolean;
  $maxWidth?: number;
  $mobileContentAlignment?: string;
  $theme?: string;
}>`
  margin: auto;
  max-width: ${({ $maxWidth }) => ($maxWidth ? `${$maxWidth}px` : '100%')};
  text-align: center;
  padding: 0 1rem;

  @media (min-width: 1024px) {
    padding: 0;
    // allowed values: center, left, bottom left
    text-align: ${({ $contentAlignment }) =>
      $contentAlignment == 'center' ? 'center' : 'left'};
  }
`;

export const ButtonContainer = styled.div<{
  $contentAlignment?: string;
  $theme?: string;
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: 30px auto 0;
  gap: 16px;

  @media (min-width: 768px) {
    flex-direction: row;
    gap: 32px;
  }

  @media (min-width: 1024px) {
    justify-content: ${({ $contentAlignment }) =>
      $contentAlignment == 'left' || $contentAlignment == 'bottom left' ? 'flex-start' : 'center'};
  }

  ${({ $theme }) =>
    $theme == 'dark'
      ? `
    > a:first-of-type {
      background-color: #fff;
      color: #000;
      &:hover { background-color: #eaeaea; }
    }
    > a:nth-of-type(2) {
      color: #fff;
      &:hover { text-decoration: underline; }
    }
  `
      : `
    > a:first-of-type {
      background-color: #000;
      color: #fff;
      &:hover { background-color: #333; }
    }
    > a:nth-of-type(2) {
      color: #000;
      &:hover { text-decoration: underline; }
    }
  `}
`;